import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Settings,
  CreditCard,
  Truck,
  Users,
  Info,
  CheckCircle,
  AlertCircle,
  Globe,
  ChevronLeft,
  Home,
  Palette,
  Crown,
  Mail,
  Phone,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrgaConfigurationContext } from '@/app/providers';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import StripeConnectManager from './StripeConnectManager';
import UserAffiliationManager from './UserAffiliationManager';
import ShippingMethodsConfig from './ShippingMethodsConfig';
import GeneralConfiguration from './GeneralConfiguration';
import PersonalizationConfiguration from './PersonalizationConfiguration';
import ContactInformationConfig from './ContactInformationConfig';
import SubscriptionPlanSection from './SubscriptionPlanSection';
import { Badge, Card, CardBody } from '@/components/ui';
import { CONTACT_INFO } from '@/shared/constants/contact';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  render: () => React.ReactNode;
  description: string;
  isEnabled?: boolean;
}

const ConfigurationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { idorga } = useParams<{ idorga: string }>();
  const { configuration, loading, refreshConfiguration, completionStatus } = useOrgaConfigurationContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    isGeneralReady,
    isStripeValid: isStripeReady,
    hasShippingMethods: hasShippingConfigured,
    hasSenderAddress,
    hasContactConfigured,
    isPortalReady,
  } = completionStatus;

  // Extraire la couleur principale de la personnalisation de l'organisation pour les icônes actives
  const getActiveIconColor = React.useMemo(() => {
    // Utiliser la personnalisation de l'organisation (button.primary) pour les icônes actives
    const buttonPrimary = configuration?.personalization?.button?.primary;
    if (!buttonPrimary) {
      return undefined; // Utilise text-primary par défaut
    }

    // Si c'est un gradient, extraire la première couleur
    if (buttonPrimary.includes('gradient') || buttonPrimary.includes('linear-gradient')) {
      const colorsMatch = buttonPrimary.match(/#[0-9A-Fa-f]{3,6}/g);
      if (colorsMatch && colorsMatch.length > 0) {
        return colorsMatch[0]; // Première couleur du gradient
      }
    }

    // Sinon, c'est une couleur unie
    return buttonPrimary;
  }, [configuration?.personalization?.button?.primary]);

  const { canManageConfiguration, canManageUsers } = usePermissionsHook();

  // Vérifier les permissions
  const hasConfigPermission = idorga ? canManageConfiguration(idorga) : false;
  const hasUsersPermission = idorga ? canManageUsers(idorga) : false;

  // Extraire la section active depuis le hash de l'URL
  const getSectionFromHash = (): string => {
    const hash = window.location.hash.replace('#', '');
    // Valider que le hash correspond à une section valide
    const validSections = ['general', 'personalization', 'subscription', 'ia', 'contact', 'payment', 'payment-connect', 'payment-apikey', 'payment-paypal', 'shipping', 'liveAssistant', 'savChat', 'users', 'info'];

    // Si c'est un sous-hash de payment, retourner payment pour la sidebar
    if (hash === 'payment-connect' || hash === 'payment-apikey' || hash === 'payment-paypal') {
      return 'payment';
    }

    return validSections.includes(hash) ? hash : 'general';
  };

  const [activeSection, setActiveSection] = useState(getSectionFromHash());

  // Synchroniser l'état avec le hash de l'URL au chargement et lors des changements
  useEffect(() => {
    const handleHashChange = () => {
      const section = getSectionFromHash();
      setActiveSection(section);
    };

    // Écouter les changements de hash
    window.addEventListener('hashchange', handleHashChange);

    // Vérifier le hash initial
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Gérer le changement de section avec mise à jour de l'URL
  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    window.location.hash = sectionId;
  };

  // Fonction pour forcer le rafraîchissement (silent pour éviter l'éclipse de l'interface)
  const handleRefresh = () => {
    if (refreshConfiguration) {
      refreshConfiguration({ silent: true });
      setRefreshKey(prev => prev + 1);
    }
  };


  // Définir les sections de navigation avec vérification des permissions
  const navItems: NavItem[] = [
    {
      id: 'general',
      label: t('admin.configuration.sections.general.title'),
      icon: Settings,
      render: () => <GeneralConfiguration />,
      description: t('admin.configuration.sections.general.description'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'personalization',
      label: t('admin.configuration.sections.personalization.title'),
      icon: Palette,
      render: () => <PersonalizationConfiguration />,
      description: t('admin.configuration.sections.personalization.description'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'subscription',
      label: t('admin.configuration.sections.subscription.title', 'Plan & Facturation'),
      icon: Crown,
      render: () => <SubscriptionPlanSection />,
      description: t('admin.configuration.sections.subscription.description', 'Gérez votre abonnement et vos commissions'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'payment',
      label: t('admin.configuration.sections.stripe.title'),
      icon: CreditCard,
      render: () => (
        <StripeConnectManager
          organizationId={idorga!}
        />
      ),
      description: t('admin.configuration.sections.stripe.description'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'shipping',
      label: t('admin.configuration.sections.shipping.title'),
      icon: Truck,
      render: () => <ShippingMethodsConfig organizationId={idorga!} onRefresh={handleRefresh} />,
      description: t('admin.configuration.sections.shipping.description'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'contact',
      label: t('admin.configuration.sections.contact.title'),
      icon: Globe,
      render: () => <ContactInformationConfig />,
      description: t('admin.configuration.sections.contact.description'),
      isEnabled: hasConfigPermission
    },
    {
      id: 'users',
      label: t('admin.configuration.sections.users.title'),
      icon: Users,
      render: () => <UserAffiliationManager organizationId={idorga!} />,
      description: t('admin.configuration.sections.users.description'),
      isEnabled: hasUsersPermission
    },
    {
      id: 'info',
      label: t('admin.configuration.sections.info.title'),
      icon: Info,
      render: () => (
        <div className="space-y-6">
          <Card variant="default">
            <CardBody className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">{t('admin.configuration.sections.info.description')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.orgId')}</span>
                    <span className="font-mono text-foreground font-semibold">{idorga}</span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.organizationStatus')}</span>
                    <Badge
                      variant={configuration?.isActive ? 'success' : 'error'}
                      size="sm"
                    >
                      {configuration?.isActive ? t('admin.configuration.general.active') : t('admin.configuration.general.inactive')}
                    </Badge>
                  </div>

                  <div className="border-b border-border pb-2 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-foreground-secondary">{t('admin.configuration.general.logo')}</span>
                      <span className={`text-sm font-medium ${configuration?.personalization?.logo?.trim() ? 'text-success' : 'text-foreground-secondary'}`}>
                        {configuration?.personalization?.logo?.trim() ? t('admin.configuration.configured') : t('admin.configuration.notConfigured')}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-secondary leading-snug">
                      {t('admin.configuration.general.logoSharedFieldHint')}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.stripeStatus')}</span>
                    <span className={`text-sm font-semibold ${isStripeReady ? 'text-success' : 'text-warning'}`}>
                      {isStripeReady ? t('admin.configuration.configured') : t('admin.configuration.notConfigured')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.deliveryStatus')}</span>
                    <span className={`text-sm font-semibold ${hasShippingConfigured ? 'text-success' : 'text-warning'}`}>
                      {hasShippingConfigured
                        ? t('admin.configuration.methodsCount', { count: configuration?.shippingConfig?.methods?.length ?? 0 })
                        : t('admin.configuration.notConfigured')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.senderAddressStatus', 'Adresse expéditeur')}</span>
                    <span className={`text-sm font-semibold ${hasSenderAddress ? 'text-success' : 'text-warning'}`}>
                      {hasSenderAddress ? t('admin.configuration.configured') : t('admin.configuration.notConfigured')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <span className="text-foreground-secondary">{t('admin.configuration.sections.contact.title')}</span>
                    <span className={`text-sm font-semibold ${hasContactConfigured ? 'text-success' : 'text-warning'}`}>
                      {hasContactConfigured
                        ? t('admin.configuration.configured')
                        : t('admin.configuration.notConfigured')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-border text-xs text-foreground-secondary flex justify-end">
                {t('admin.configuration.date')}: {configuration?.updatedAt ? new Date(configuration.updatedAt).toLocaleDateString('fr-FR') : 'N/A'}
              </div>
            </CardBody>
          </Card>

          {/* Section Besoin d'aide */}
          <Card variant="default" className="border-primary/20">
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Info className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Besoin d'aide ?</h3>
              </div>

              <p className="text-sm text-foreground-secondary mb-6">
                Notre équipe est là pour vous accompagner dans la configuration de votre espace de vente. N'hésitez pas à nous contacter pour une démo personnalisée ou un tutoriel.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href={`mailto:${CONTACT_INFO.EMAIL}?subject=Demande d'aide - ${configuration?.name || idorga}`}
                  className="flex items-center gap-3 p-4 bg-background-tertiary hover:bg-background-secondary rounded-xl border border-border hover:border-primary/30 transition-all group"
                >
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">Contactez-nous par email</p>
                    <p className="text-xs text-foreground-secondary mt-0.5">{CONTACT_INFO.EMAIL}</p>
                  </div>
                </a>

                <a
                  href={`tel:${CONTACT_INFO.PHONE}`}
                  className="flex items-center gap-3 p-4 bg-background-tertiary hover:bg-background-secondary rounded-xl border border-border hover:border-primary/30 transition-all group"
                >
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">Appelez-nous</p>
                    <p className="text-xs text-foreground-secondary mt-0.5">{CONTACT_INFO.PHONE_DISPLAY}</p>
                  </div>
                </a>
              </div>

              <div className="mt-4 p-4 bg-info/5 border border-info/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-info-dark">
                    <p className="font-semibold mb-1">Demandez une démo personnalisée</p>
                    <p className="text-xs">
                      Nous pouvons vous accompagner dans la configuration complète de votre espace de vente et vous montrer toutes les fonctionnalités disponibles.
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      ),
      description: t('admin.configuration.sections.info.description'),
      isEnabled: true
    }
  ];

  const activeNavItem = navItems.find(item => item.id === activeSection);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-foreground-secondary">{t('admin.configuration.loading')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:hidden bg-surface border-b border-border sticky top-0 z-10">
          <div className="flex items-center justify-between p-3">
            <div className="text-base font-semibold text-foreground">{t('admin.configuration.title')}</div>
          </div>
          <div className="px-3 pb-3">
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {navItems.filter(item => item.isEnabled).map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={`mobile-${item.id}`}
                    onClick={() => handleSectionChange(item.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg whitespace-nowrap transition-all duration-200 text-xs font-medium ${isActive
                      ? 'text-white shadow-md'
                      : 'bg-background-tertiary text-foreground-secondary hover:bg-border hover:text-foreground'
                      }`}
                    style={isActive && getActiveIconColor ? { backgroundColor: getActiveIconColor } : isActive ? { backgroundColor: 'var(--primary)' } : undefined}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile Status Indicators - Simplifié */}
            {!isPortalReady && (
              <Card variant="default" className="mt-3">
                <CardBody className="p-3">
                  <h3 className="text-xs font-semibold text-foreground mb-2">{t('admin.configuration.status')}</h3>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-secondary">{t('admin.configuration.sections.general.title')}</span>
                      {isGeneralReady ? (
                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-secondary">{t('admin.configuration.stripeStatus')}</span>
                      {isStripeReady ? (
                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-secondary">{t('admin.configuration.deliveryStatus')}</span>
                      {hasShippingConfigured ? (
                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-secondary">{t('admin.configuration.senderAddressStatus', 'Adresse expéditeur')}</span>
                      {hasSenderAddress ? (
                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-secondary">{t('admin.configuration.sections.contact.title')}</span>
                      {hasContactConfigured ? (
                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
        {/* Sidebar */}
        <div className="hidden lg:block lg:w-80 fixed left-16 top-16 bottom-0 bg-surface border-r border-border z-20">
          <div className="h-full overflow-y-auto">
            <div className="p-4 mt-2.5">
              <nav className="space-y-1.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  const isDisabled = !item.isEnabled;

                  // Indicateurs de complétion par section (mêmes règles que dans "État de la configuration")
                  let isSectionReady = false;
                  if (item.id === 'general') {
                    isSectionReady = isGeneralReady;
                  } else if (item.id === 'payment') {
                    isSectionReady = isStripeReady;
                  } else if (item.id === 'shipping') {
                    isSectionReady = hasShippingConfigured && hasSenderAddress;
                  } else if (item.id === 'contact') {
                    isSectionReady = hasContactConfigured;
                  }

                  return (
                    <button
                      key={item.id}
                      onClick={() => !isDisabled && handleSectionChange(item.id)}
                      disabled={isDisabled}
                      className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg transition-all duration-200 ${isActive
                        ? 'bg-link-active-10 text-link-active border-l-4 border-link-active shadow-sm font-semibold'
                        : isDisabled
                          ? 'text-foreground-secondary cursor-not-allowed opacity-50'
                          : 'text-foreground-secondary hover:bg-background-tertiary hover:text-foreground'
                        }`}
                    >
                      <div
                        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive
                          ? 'bg-primary shadow-md'
                          : isDisabled
                            ? 'bg-background-tertiary text-foreground-secondary'
                            : 'bg-background-tertiary text-foreground-secondary'
                          }`}
                      >
                        <Icon
                          className={`w-4 h-4 ${isActive ? 'text-white' : ''}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm break-words ${isActive ? 'font-bold' : 'font-semibold'}`}>{item.label}</span>
                          {isSectionReady && !isDisabled && (
                            <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                          )}
                          {!isSectionReady && !isDisabled && (item.id === 'general' || item.id === 'payment' || item.id === 'shipping' || item.id === 'contact') && (
                            <AlertCircle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs text-foreground-secondary mt-0.5 break-words whitespace-normal">{item.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Status indicators - Simplifié */}
              {!isPortalReady && (
                <Card variant="default" className="mt-6">
                  <CardBody className="p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-2">{t('admin.configuration.status')}</h3>
                    <div className="space-y-2">
                      {/* Organisation */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-secondary">{t('admin.configuration.sections.general.title')}</span>
                        {isGeneralReady ? (
                          <CheckCircle className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                        )}
                      </div>

                      {/* Paiement */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-secondary">
                          {t('admin.configuration.stripeStatus')}
                        </span>
                        {isStripeReady ? (
                          <CheckCircle className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                        )}
                      </div>

                      {/* Livraison */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-secondary">{t('admin.configuration.deliveryStatus')}</span>
                        {hasShippingConfigured ? (
                          <CheckCircle className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                        )}
                      </div>

                      {/* Adresse expéditeur */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-secondary">{t('admin.configuration.senderAddressStatus', 'Adresse expéditeur')}</span>
                        {hasSenderAddress ? (
                          <CheckCircle className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                        )}
                      </div>

                      {/* Contact */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-secondary">{t('admin.configuration.sections.contact.title')}</span>
                        {hasContactConfigured ? (
                          <CheckCircle className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full bg-border-medium" />
                        )}
                      </div>

                      {/* Avertissement si la configuration n'est pas complète */}
                      {!isPortalReady && (
                        <div className="mt-3 p-2 bg-warning-10 border border-warning-20 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
                            <div className="text-[11px] text-warning-dark">
                              <p className="font-medium">{t('admin.configuration.platformInaccessible')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 lg:ml-80">
          {/* Breadcrumb Navigation */}
          <div className="bg-surface border-b border-border shadow-sm sticky top-0 lg:relative z-10">
            <div className="py-3 sm:py-4 px-4 sm:px-6">
              <nav className="flex items-center justify-between lg:justify-end gap-2 text-xs sm:text-sm">
                <Link
                  to="/organizations/manage"
                  className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-primary hover:text-primary-dark hover:bg-primary-20 transition-all duration-200 font-medium group"
                >
                  <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:-translate-x-1 transition-transform" />
                  <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{t('admin.configuration.breadcrumb.organizations')}</span>
                </Link>
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="text-foreground-secondary">/</span>
                  <span className="text-foreground font-medium truncate">
                    {t('admin.configuration.title')}
                  </span>
                </div>
              </nav>
            </div>
          </div>
          <div
            className="p-6"
            key={refreshKey}
          >
            {activeNavItem && !activeNavItem.isEnabled ? (
              <div className="space-y-6">
                <Card variant="outlined" className="border-warning-20 bg-warning-10">
                  <CardBody className="p-6">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-warning mr-3" />
                      <div>
                        <p className="text-warning-dark font-semibold">
                          {t('admin.configuration.insufficientPermissions')}
                        </p>
                        <p className="text-warning-dark/80 text-sm mt-1">
                          {activeSection === 'users'
                            ? t('admin.configuration.users.noPermissionsDesc')
                            : t('admin.configuration.noConfigPermission')}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ) : (
              activeNavItem?.render && activeNavItem.render()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationDashboard;
